import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn, formatCurrency } from '../../lib/utils';
import { Search, DollarSign, FileText, CheckCircle2, Clock, Download, RefreshCw, MoreHorizontal, Upload, CreditCard, AlertTriangle, Printer } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const INITIAL_PAYMENTS = [
  { id: 'PAY-001', eraId: 'ERA-2026-001', payer: 'Blue Cross', checkNum: '1234567', amount: 12450.00, claimsCount: 18, received: '2026-01-30', status: 'posted' },
  { id: 'PAY-002', eraId: 'ERA-2026-002', payer: 'Medicare', checkNum: 'EFT-89012', amount: 8925.00, claimsCount: 24, received: '2026-01-29', status: 'posted' },
  { id: 'PAY-003', eraId: 'ERA-2026-003', payer: 'Aetna', checkNum: '3456789', amount: 6780.00, claimsCount: 12, received: '2026-01-28', status: 'pending' },
  { id: 'PAY-004', eraId: 'ERA-2026-004', payer: 'UnitedHealth', checkNum: 'EFT-34567', amount: 15200.00, claimsCount: 22, received: '2026-01-27', status: 'posted' },
  { id: 'PAY-005', eraId: 'ERA-2026-005', payer: 'Cigna', checkNum: '5678901', amount: 4350.00, claimsCount: 8, received: '2026-01-26', status: 'exception' },
];

const DEMO_ERA_DETAILS = [
  { claimId: 'CLM-001', patient: 'Sarah Johnson', patientId: '1', billed: 255.00, allowed: 198.50, paid: 158.80, patientResp: 39.70, adjustments: 56.50, status: 'paid' },
  { claimId: 'CLM-002', patient: 'Michael Brown', patientId: '2', billed: 185.00, allowed: 145.00, paid: 116.00, patientResp: 29.00, adjustments: 40.00, status: 'paid' },
  { claimId: 'CLM-003', patient: 'Emily Davis', patientId: '3', billed: 350.00, allowed: 280.00, paid: 224.00, patientResp: 56.00, adjustments: 70.00, status: 'paid' },
  { claimId: 'CLM-004', patient: 'Robert Wilson', patientId: '4', billed: 1250.00, allowed: 0.00, paid: 0.00, patientResp: 0.00, adjustments: 1250.00, status: 'denied' },
];

const PAYMENT_TREND = [
  { month: 'Aug', payments: 125000, adjustments: 18500 },
  { month: 'Sep', payments: 132000, adjustments: 19200 },
  { month: 'Oct', payments: 128000, adjustments: 17800 },
  { month: 'Nov', payments: 145000, adjustments: 21000 },
  { month: 'Dec', payments: 138000, adjustments: 19500 },
  { month: 'Jan', payments: 152000, adjustments: 22100 },
];

const RevQPayments = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedERA, setSelectedERA] = useState(null);
  const [payments, setPayments] = useState(INITIAL_PAYMENTS);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalType, setModalType] = useState(null);

  const openModal = (payment, type, e) => {
    if (e) e.stopPropagation();
    setSelectedPayment(payment);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedPayment(null);
    setModalType(null);
  };

  const handlePostPayments = () => {
    setPayments(prev => prev.map(p =>
      p.id === selectedPayment.id ? { ...p, status: 'posted' } : p
    ));
    toast.success(`${selectedPayment.eraId} payments posted successfully`, {
      description: `${selectedPayment.claimsCount} claims totaling ${formatCurrency(selectedPayment.amount)}`
    });
    closeModal();
  };

  const handleDownloadERA = (payment, e) => {
    if (e) e.stopPropagation();
    toast.success(`Downloading ${payment.eraId}`, { description: 'ERA file download started' });
  };

  const handlePrintReport = (payment, e) => {
    if (e) e.stopPropagation();
    toast.success(`Printing report for ${payment.eraId}`, { description: 'Sent to default printer' });
  };

  const getStatusBadge = (status) => {
    const configs = {
      posted: { label: 'Posted', class: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
      pending: { label: 'Pending', class: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
      exception: { label: 'Exception', class: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return <Badge className={cn("gap-1", config.class)}><Icon className="h-3 w-3" />{config.label}</Badge>;
  };

  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const pendingPayments = payments.filter(p => p.status === 'pending');

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Payments & ERA</h1>
          <p className="text-slate-500 mt-1">Process electronic remittance and track payments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2"><Upload className="h-4 w-4" /> Import ERA</Button>
          <Button className="bg-black hover:bg-gray-800 gap-2"><RefreshCw className="h-4 w-4" /> Sync Payments</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><DollarSign className="h-5 w-5 text-emerald-600" /></div><div><p className="text-sm text-muted-foreground">MTD Payments</p><p className="text-xl font-bold text-emerald-600">{formatCurrency(totalPayments)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><FileText className="h-5 w-5 text-blue-600" /></div><div><p className="text-sm text-muted-foreground">ERAs Received</p><p className="text-xl font-bold">{payments.length}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="h-5 w-5 text-amber-600" /></div><div><p className="text-sm text-muted-foreground">Pending Post</p><p className="text-xl font-bold text-amber-600">{pendingPayments.length}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center"><CreditCard className="h-5 w-5 text-slate-600" /></div><div><p className="text-sm text-muted-foreground">Avg Payment</p><p className="text-xl font-bold">{formatCurrency(totalPayments / payments.length)}</p></div></div></CardContent></Card>
      </div>

      {/* Payment Trend Chart */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-semibold">Payment Trend</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={PAYMENT_TREND}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis tickFormatter={(v) => `$${v/1000}k`} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Line type="monotone" dataKey="payments" name="Payments" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="adjustments" name="Adjustments" stroke="#f59e0b" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* ERA List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">ERA / Payment Batches</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search ERAs..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="era-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ERA ID</TableHead>
                <TableHead>Payer</TableHead>
                <TableHead>Check/EFT #</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Claims</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedERA(payment)} data-testid={`payment-row-${payment.id}`}>
                  <TableCell className="font-mono text-sm">{payment.eraId}</TableCell>
                  <TableCell className="font-medium">{payment.payer}</TableCell>
                  <TableCell className="font-mono text-sm">{payment.checkNum}</TableCell>
                  <TableCell className="text-right font-medium text-emerald-600">{formatCurrency(payment.amount)}</TableCell>
                  <TableCell className="text-right">{payment.claimsCount}</TableCell>
                  <TableCell>{payment.received}</TableCell>
                  <TableCell>{getStatusBadge(payment.status)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`payment-menu-${payment.id}`} onClick={(e) => e.stopPropagation()}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`payment-details-${payment.id}`} onSelect={(e) => openModal(payment, 'details')}>
                          <FileText className="h-4 w-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`payment-post-${payment.id}`} onSelect={(e) => openModal(payment, 'post')}>
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Post Payments
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`payment-download-${payment.id}`} onSelect={() => handleDownloadERA(payment)}>
                          <Download className="h-4 w-4 mr-2" /> Download ERA
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`payment-print-${payment.id}`} onSelect={() => handlePrintReport(payment)}>
                          <Printer className="h-4 w-4 mr-2" /> Print Report
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ERA Detail Panel */}
      {selectedERA && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">ERA Details: {selectedERA.eraId}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedERA(null)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim ID</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="text-right">Allowed</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Patient Resp</TableHead>
                  <TableHead className="text-right">Adjustments</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {DEMO_ERA_DETAILS.map((detail) => (
                  <TableRow key={detail.claimId}>
                    <TableCell className="font-mono text-sm">{detail.claimId}</TableCell>
                    <TableCell>
                      <button onClick={() => navigate(`/revq/patients/${detail.patientId}`)} className="font-medium text-slate-800 hover:text-violet-600 hover:underline transition-colors cursor-pointer">{detail.patient}</button>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(detail.billed)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(detail.allowed)}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">{formatCurrency(detail.paid)}</TableCell>
                    <TableCell className="text-right text-blue-600">{formatCurrency(detail.patientResp)}</TableCell>
                    <TableCell className="text-right text-amber-600">{formatCurrency(detail.adjustments)}</TableCell>
                    <TableCell>
                      <Badge className={detail.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>{detail.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* View Details Modal */}
      <Dialog open={modalType === 'details'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-lg" data-testid="payment-details-modal">
          <DialogHeader>
            <DialogTitle>Payment Details: {selectedPayment?.eraId}</DialogTitle>
            <DialogDescription>Complete payment batch information</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['ERA ID', selectedPayment?.eraId],
              ['Payer', selectedPayment?.payer],
              ['Check/EFT #', selectedPayment?.checkNum],
              ['Amount', formatCurrency(selectedPayment?.amount || 0)],
              ['Claims Count', selectedPayment?.claimsCount],
              ['Received', selectedPayment?.received],
              ['Status', selectedPayment?.status],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium capitalize">{value}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Payments Confirmation Modal */}
      <Dialog open={modalType === 'post'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-md" data-testid="post-payments-modal">
          <DialogHeader>
            <DialogTitle>Post Payments</DialogTitle>
            <DialogDescription>Confirm posting payments from {selectedPayment?.eraId}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-slate-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Payer</span><span className="font-medium">{selectedPayment?.payer}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Check/EFT</span><span className="font-mono">{selectedPayment?.checkNum}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Amount</span><span className="font-bold text-emerald-600">{formatCurrency(selectedPayment?.amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Claims</span><span className="font-medium">{selectedPayment?.claimsCount} claims</span></div>
            </div>
            {selectedPayment?.status === 'posted' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-700">This payment batch has already been posted.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button
              onClick={handlePostPayments}
              disabled={selectedPayment?.status === 'posted'}
              data-testid="confirm-post-btn"
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              <CheckCircle2 className="h-4 w-4" /> Post Payments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevQPayments;
